import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import LinkButton from '@/components/Button/LinkButton';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import DescirptionContainer from '@/components/DescriptionList/DescriptionContainer';
import DescriptionRow from '@/components/DescriptionList/DescriptionRow';
import { H3, H4 } from '@/components/Element/Heading';
import VerticalLeadFormSection from '@/components/LeadForm/VerticalLeadFormSection';
import MetaTags from '@/components/Head/MetaTags';
import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import ListableImage from '@/components/Listable/ListableImage';
import { getBatteryBySlug, getManufacturerBySlug } from 'data/database';
import { Manufacturer } from 'data/types';
import { Battery } from 'data/types';
import { YEAR } from 'lib/constants';
import { ProductJsonLd } from 'next-seo';
import Link from 'next/link';

export async function getStaticPaths() {
  // const batteries = await getBatteries();
  return {
    paths: [],
    // batteries.map((battery) => ({
    //   params: {
    //     manufacturer_slug: battery.manufacturerSlug,
    //     battery_slug: battery.slug,
    //   },
    // })),
    fallback: 'blocking', // false or 'blocking'
  };
}

type StaticProps = {
  params: {
    manufacturer_slug: string;
    battery_slug: string;
  };
};

export async function getStaticProps({
  params: { manufacturer_slug, battery_slug },
}: StaticProps) {
  const manufacturer = await getManufacturerBySlug(manufacturer_slug);
  const battery = await getBatteryBySlug(battery_slug);
  return { props: { manufacturer, battery } };
}

type Props = {
  manufacturer: Manufacturer;
  battery: Battery;
};

export default function Page({ manufacturer, battery }: Props) {
  return (
    <article>
      <MetaTags
        title={`${manufacturer.name} ${battery.name} Solar Battery Manufacturer Guide`}
        description={`Learn about the ${manufacturer.name} ${battery.name} solar battery. Explore solar battery efficiency, warranties, chemistry and more.`}
      />
      <ProductJsonLd
        productName={battery.name}
        brand={manufacturer.name}
        manufacturerName={manufacturer.name}
        manufacturerLogo={manufacturer.webpPath}
        images={
          battery.webpPath && battery.webpPath !== 'null'
            ? [battery.webpPath]
            : []
        }
        offers={[
          {
            lowPrice: 6000,
            price: 12000,
            highPrice: 100000,
            url: `https://energypal.com/best-home-battery-systems/${manufacturer.slug}/${battery.slug}`,
            priceCurrency: 'USD',
            priceValidUntil: '2024-01-01',
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'EnergyPal',
              url: 'https://energypal.com',
            },
          },
        ]}
      />
      <SimpleCenteredHeaderSection
        dark
        supTitle={`Buyer's Guide ${YEAR}`}
        title={`${manufacturer.name} ${battery.name}`}
        subTitle="Solar Battery Spec Datasheet"
      />
      <div className="hidden md:block">
        <FullWidthBarBreadcrumbs
          pages={[
            {
              name: 'Resources',
              href: '/solar-customer-resources',
            },
            {
              name: 'Best Home Battery Systems',
              href: '/best-home-battery-systems',
            },
            {
              name: manufacturer.name,
              href: `/best-home-battery-systems/${manufacturer.slug}`,
            },
            {
              name: battery.name,
              href: `/best-home-battery-systems/${manufacturer.slug}/${battery.slug}`,
            },
          ]}
        />
      </div>
      <CenteredContentSection>
        <ListableImage listable={battery} className="text-center" />
        {/* <ManufacturerLogo manufacturer={manufacturer} className="text-center" /> */}
        <DescirptionContainer>
          <DescriptionRow label="Model">{battery.name}</DescriptionRow>
          <DescriptionRow label="Manufacturer">
            <Link href={`/best-home-battery-systems/${manufacturer.slug}`}>
              {manufacturer.name}
            </Link>
          </DescriptionRow>
          <DescriptionRow label="Total Capacity">
            {battery.totalCapacity}
          </DescriptionRow>
          <DescriptionRow label="Warranty">
            {battery.warrantyYears}
          </DescriptionRow>
          <DescriptionRow label="Model Year">
            {battery.modelYear}
          </DescriptionRow>
          {/* <DescriptionRow label="About">{manufacturer.intro}</DescriptionRow> */}
        </DescirptionContainer>
        {battery.datasheetPath !== 'null' &&
          battery.datasheetPath !== undefined &&
          battery.datasheetPath !== null && (
            <>
              <div className="text-center">
                <H3>
                  {manufacturer.name} Solar Battery Spec Datasheet{' '}
                  {battery.name}
                </H3>
                <LinkButton href={battery.datasheetPath}>
                  Download Datasheet
                </LinkButton>
              </div>
            </>
          )}

        <H4>Battery Info</H4>
        <DescirptionContainer>
          {battery.characteristics.map((characteristic) => (
            <DescriptionRow key={characteristic.key} label={characteristic.key}>
              {characteristic.value}
            </DescriptionRow>
          ))}
          {battery.certifications && battery.certifications?.length > 0 && (
            <DescriptionRow label="Certifications">
              <ul>
                {battery.certifications.map((certification) => (
                  <li key={certification.name}>
                    <a
                      href={certification.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {certification.name}
                    </a>
                  </li>
                ))}
              </ul>
            </DescriptionRow>
          )}
        </DescirptionContainer>
        {battery.warrantyDetails && (
          <>
            <H4>Warranty</H4>
            <DescirptionContainer>
              {battery.warrantyDetails.map((characteristic) => (
                <DescriptionRow
                  key={characteristic.key}
                  label={characteristic.key}
                >
                  {characteristic.value}
                </DescriptionRow>
              ))}
            </DescirptionContainer>
          </>
        )}
      </CenteredContentSection>
      <VerticalLeadFormSection />
    </article>
  );
}
